import React from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import { getBlogArticleBySlug } from "./blogArticles";

const BLOG_URL = "https://meshum.com/blog";
const MARKET_LINKS = [
  {
    to: "/search",
    title: "Search used cars with market context"
  },
  {
    to: "/search?zip=33131&radius=50",
    title: "Compare used cars near Miami"
  },
  {
    to: "/used-cars/toyota/camry",
    title: "Review Toyota Camry listings"
  }
];

const MIAMI_BEST_DEALS_ARTICLES = new Set([
  "best-time-to-buy-a-used-car-in-miami",
  "used-hybrid-ev-prices-in-miami",
  "how-to-find-the-best-used-car-deals"
]);

const MIAMI_RESEARCH_ARTICLES = new Set([
  "best-time-to-buy-a-used-car-in-miami",
  "used-hybrid-ev-prices-in-miami"
]);

function BlogArticle() {
  const { slug } = useParams();
  const article = getBlogArticleBySlug(slug);
  let marketLinks = MIAMI_BEST_DEALS_ARTICLES.has(slug)
    ? [{ to: "/used-cars/miami-fl/best-deals", title: "Browse the best used car deals near Miami" }, ...MARKET_LINKS]
    : MARKET_LINKS;
  if (MIAMI_RESEARCH_ARTICLES.has(slug)) {
    marketLinks = [
      { to: "/research/miami-used-car-market-august-2026", title: "Read the August 2026 Miami used-car market report" },
      ...marketLinks
    ];
  }

  if (!article) {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-12 text-center">
        <h1 className="text-3xl font-semibold text-slate-950">Article not found</h1>
        <Link to="/blog" className="mt-5 inline-flex text-sm font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4">
          Back to blog
        </Link>
      </main>
    );
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": ["Article", "BlogPosting"],
    headline: article.title,
    description: article.description,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    mainEntityOfPage: article.canonicalUrl,
    url: article.canonicalUrl,
    image: article.seo.image,
    publisher: {
      "@type": "Organization",
      name: "Meshum",
      logo: {
        "@type": "ImageObject",
        url: "https://meshumimages.blob.core.windows.net/websiteimages/1.png"
      }
    },
    author: {
      "@type": "Organization",
      name: "Meshum"
    }
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://meshum.com/"
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: BLOG_URL
      },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
        item: article.canonicalUrl
      }
    ]
  };

  const renderText = (value) => {
    if (!Array.isArray(value)) {
      return value;
    }

    return value.map((part, index) => {
      if (typeof part === "string") {
        return part;
      }

      if (part.type === "link") {
        return (
          <Link
            key={`${part.to}-${index}`}
            to={part.to}
            className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-800"
          >
            {part.text}
          </Link>
        );
      }

      return null;
    });
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
      <Helmet>
        <title>{article.seo.title}</title>
        <meta name="description" content={article.seo.description} />
        <link rel="canonical" href={article.canonicalUrl} />
        <meta property="og:title" content={article.seo.ogTitle || article.title} />
        <meta property="og:description" content={article.seo.ogDescription || article.description} />
        <meta property="og:url" content={article.canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={article.seo.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.seo.ogTitle || article.title} />
        <meta name="twitter:description" content={article.seo.ogDescription || article.description} />
        <meta name="twitter:image" content={article.seo.image} />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        {article.schema?.faq && (
          <script type="application/ld+json">{JSON.stringify(article.schema.faq)}</script>
        )}
      </Helmet>

      <article className="mx-auto w-full max-w-[820px]">
        <Link to="/blog" className="inline-flex text-sm font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-800">
          Back to blog
        </Link>

        <div className="mt-6 border-b border-slate-200 pb-8 sm:pb-10">
          <div className="text-sm font-medium text-slate-500">
            {new Date(`${article.publishDate}T00:00:00`).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric"
            })}
          </div>
          <h1 className="mt-4 max-w-none text-3xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-5xl">
            {article.title}
          </h1>
          <p className="mt-5 max-w-[760px] text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
            {article.description}
          </p>
        </div>

        <div className="mt-8 space-y-6 text-base leading-8 text-slate-700 sm:mt-10 sm:text-lg sm:leading-9">
          {article.content.map((block, index) => {
            if (block.type === "heading") {
              return (
                <h2 key={index} className="pt-6 text-2xl font-semibold leading-snug tracking-tight text-slate-950 sm:pt-8 sm:text-3xl">
                  {block.text}
                </h2>
              );
            }

            if (block.type === "subheading") {
              return (
                <h3 key={index} className="pt-4 text-xl font-semibold leading-snug tracking-tight text-slate-950 sm:text-2xl">
                  {block.text}
                </h3>
              );
            }

            if (block.type === "quote") {
              return (
                <blockquote key={index} className="rounded-2xl border-l-4 border-blue-600 bg-white px-5 py-4 text-lg font-semibold leading-8 text-slate-950 shadow-sm sm:px-6 sm:py-5 sm:text-xl">
                  {block.text}
                </blockquote>
              );
            }

            if (block.type === "list") {
              return (
                <ul key={index} className="list-disc space-y-3 pl-6">
                  {block.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              );
            }

            return <p key={index}>{renderText(block.text)}</p>;
          })}
        </div>

        <section className="mt-12 border-t border-slate-200 pt-8">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-950">Compare Listings With Market Context</h2>
          <p className="mt-4 text-base leading-7 text-slate-600">
            Use Meshum's pricing guides alongside active inventory, nearby alternatives, recently sold comparable vehicles, and the Meshum Estimate.
          </p>
          <ul className="mt-5 space-y-3">
            {marketLinks.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-800"
                >
                  {link.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {article.internalLinks?.length > 0 && (
          <section className="mt-12 border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-950">Related Articles</h2>
            <ul className="mt-5 space-y-3">
              {article.internalLinks.map((link) => (
                <li key={link.slug}>
                  <Link
                    to={`/blog/${link.slug}`}
                    className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-800"
                  >
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </main>
  );
}

export default BlogArticle;
